/**
 * Client-side seller intake validation and normalization (Sell Your Car M4).
 */
import {
  SELLER_DEFAULT_CURRENCY,
  SELLER_OWNERSHIP_STATUSES,
  SELLER_PREFERRED_CONTACT_METHODS,
  SELLER_TYPES,
} from "./sellerIntakeConstants";
import { normalizeSellerSubmissionInput } from "./sellerIntakeNormalization";
import { SELLER_VALIDATION_CODES } from "../../intl/validationMessages.js";

/** @deprecated Use SELLER_VALIDATION_CODES — values are catalog keys for client translation. */
export const SELLER_VALIDATION_MESSAGES_ES = SELLER_VALIDATION_CODES;

export const SELLER_VALIDATION_FIELD_ORDER = Object.freeze([
  "sellerType",
  "sellerName",
  "sellerPhone",
  "sellerEmail",
  "preferredContactMethod",
  "city",
  "department",
  "vehicleMake",
  "vehicleModel",
  "vehicleYear",
  "vehicleTrim",
  "vehicleMileageKm",
  "askingPrice",
  "currency",
  "ownershipStatus",
  "conditionSummary",
  "notes",
  "consentAccepted",
]);

const SELLER_NAME_MIN = 2;
const SELLER_NAME_MAX = 80;
const SELLER_EMAIL_MAX = 120;
const CITY_MIN = 2;
const CITY_MAX = 80;
const DEPARTMENT_MAX = 80;
const MAKE_MIN = 2;
const MAKE_MAX = 50;
const MODEL_MAX = 60;
const TRIM_MAX = 80;
const YEAR_MIN = 1950;
const MILEAGE_MAX = 1_000_000;
const PRICE_MAX = 2_000_000_000;
const CONDITION_MAX = 500;
const NOTES_MAX = 1000;
const PLATE_MAX = 20;
const VIN_MAX = 30;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_LETTER_PATTERN = /[\p{L}]/u;
const VALID_SELLER_TYPES = new Set(Object.values(SELLER_TYPES));
const VALID_CONTACT_METHODS = new Set(Object.values(SELLER_PREFERRED_CONTACT_METHODS));
const VALID_OWNERSHIP_STATUSES = new Set(Object.values(SELLER_OWNERSHIP_STATUSES));
const VALID_CURRENCIES = new Set(["COP", "USD"]);
const PHONE_REQUIRED_METHODS = new Set([
  SELLER_PREFERRED_CONTACT_METHODS.WHATSAPP,
  SELLER_PREFERRED_CONTACT_METHODS.PHONE,
  SELLER_PREFERRED_CONTACT_METHODS.ANY,
]);

const FIELD_ID_MAP = Object.freeze({
  sellerType: "seller-intake-seller-type",
  sellerName: "seller-intake-seller-name",
  sellerPhone: "seller-intake-seller-phone",
  sellerEmail: "seller-intake-seller-email",
  preferredContactMethod: "seller-intake-contact-method",
  city: "seller-intake-city",
  department: "seller-intake-department",
  vehicleMake: "seller-intake-vehicle-make",
  vehicleModel: "seller-intake-vehicle-model",
  vehicleYear: "seller-intake-vehicle-year",
  vehicleTrim: "seller-intake-vehicle-trim",
  vehicleMileageKm: "seller-intake-vehicle-mileage",
  askingPrice: "seller-intake-asking-price",
  currency: "seller-intake-currency",
  ownershipStatus: "seller-intake-ownership",
  conditionSummary: "seller-intake-condition",
  notes: "seller-intake-notes",
  consentAccepted: "seller-intake-consent",
});

function pickString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function countDigits(value) {
  return pickString(value).replace(/\D/g, "").length;
}

function hasUsableName(value) {
  return NAME_LETTER_PATTERN.test(value);
}

function isOnlyPunctuationOrSpaces(value) {
  return /^[\p{P}\p{S}\s]+$/u.test(value);
}

function isRepeatedCharacters(value) {
  return /^(.)\1{9,}$/u.test(value.replace(/\s/g, ""));
}

function getMaxVehicleYear() {
  return new Date().getFullYear() + 1;
}

function isBlankNumeric(value) {
  return value == null || value === "";
}

/**
 * @param {Record<string, string|null|undefined>} errors
 * @returns {boolean}
 */
export function hasSellerSubmissionErrors(errors = {}) {
  return Object.values(errors).some((message) => Boolean(message));
}

/**
 * @param {Record<string, string|null|undefined>} errors
 * @returns {string|null}
 */
export function getFirstSellerSubmissionErrorField(errors = {}) {
  return SELLER_VALIDATION_FIELD_ORDER.find((field) => errors[field]) || null;
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput|object} input
 * @param {object} [options]
 * @returns {Record<string, string>}
 */
export function getSellerSubmissionFieldErrors(input = {}, options = {}) {
  return validateSellerSubmissionInput(input, options).fieldErrors;
}

/**
 * @param {import('./sellerIntakeTypes').SellerSubmissionInput|object} input
 * @param {object} [options]
 * @returns {{
 *   valid: boolean,
 *   ok: boolean,
 *   errors: Record<string, string>,
 *   fieldErrors: Record<string, string>,
 *   formError: string|null,
 *   warnings: string[],
 *   spam?: boolean,
 *   normalized?: import('./sellerIntakeTypes').SellerSubmissionInput,
 * }}
 */
export function validateSellerSubmissionInput(input = {}, options = {}) {
  const normalized = options.skipNormalize ? input : normalizeSellerSubmissionInput(input);
  /** @type {Record<string, string>} */
  const errors = {};
  const warnings = [];

  if (pickString(normalized.honeypot)) {
    return {
      valid: false,
      ok: false,
      errors: {},
      fieldErrors: {},
      formError: SELLER_VALIDATION_CODES.formSpam,
      warnings: ["honeypot_triggered"],
      spam: true,
      normalized,
    };
  }

  const rawSellerType = pickString(input.sellerType);
  if (!rawSellerType || !VALID_SELLER_TYPES.has(rawSellerType)) {
    errors.sellerType = SELLER_VALIDATION_CODES.sellerTypeRequired;
  }

  const sellerName = normalized.sellerName;
  if (!sellerName) {
    errors.sellerName = SELLER_VALIDATION_CODES.sellerNameRequired;
  } else if (sellerName.length < SELLER_NAME_MIN) {
    errors.sellerName = SELLER_VALIDATION_CODES.sellerNameTooShort;
  } else if (sellerName.length > SELLER_NAME_MAX) {
    errors.sellerName = SELLER_VALIDATION_CODES.sellerNameTooLong;
  } else if (!hasUsableName(sellerName) || isOnlyPunctuationOrSpaces(sellerName)) {
    errors.sellerName = SELLER_VALIDATION_CODES.sellerNameInvalid;
  }

  const preferredContactMethod =
    normalized.preferredContactMethod || SELLER_PREFERRED_CONTACT_METHODS.WHATSAPP;
  if (!VALID_CONTACT_METHODS.has(preferredContactMethod)) {
    errors.preferredContactMethod = SELLER_VALIDATION_CODES.preferredContactInvalid;
  }

  const sellerPhone = normalized.sellerPhone;
  const sellerEmail = normalized.sellerEmail;
  const phoneRequired = PHONE_REQUIRED_METHODS.has(preferredContactMethod);
  const emailRequired = preferredContactMethod === SELLER_PREFERRED_CONTACT_METHODS.EMAIL;

  if (phoneRequired && !sellerPhone) {
    errors.sellerPhone = SELLER_VALIDATION_CODES.sellerPhoneRequired;
  } else if (sellerPhone) {
    const digits = countDigits(sellerPhone);
    if (digits < PHONE_MIN_DIGITS) {
      errors.sellerPhone = SELLER_VALIDATION_CODES.sellerPhoneInvalid;
    } else if (digits > PHONE_MAX_DIGITS) {
      errors.sellerPhone = SELLER_VALIDATION_CODES.sellerPhoneTooLong;
    }
  }

  if (emailRequired && !sellerEmail) {
    errors.sellerEmail = SELLER_VALIDATION_CODES.sellerEmailRequired;
  } else if (sellerEmail) {
    if (sellerEmail.length > SELLER_EMAIL_MAX || !EMAIL_PATTERN.test(sellerEmail)) {
      errors.sellerEmail = SELLER_VALIDATION_CODES.sellerEmailInvalid;
    }
  }

  const city = normalized.city;
  if (!city) {
    errors.city = SELLER_VALIDATION_CODES.cityRequired;
  } else if (city.length < CITY_MIN) {
    errors.city = SELLER_VALIDATION_CODES.cityTooShort;
  } else if (city.length > CITY_MAX) {
    errors.city = SELLER_VALIDATION_CODES.cityTooLong;
  }

  const department = normalized.department;
  if (department && department.length > DEPARTMENT_MAX) {
    errors.department = SELLER_VALIDATION_CODES.departmentTooLong;
  }

  const vehicleMake = normalized.vehicleMake;
  if (!vehicleMake) {
    errors.vehicleMake = SELLER_VALIDATION_CODES.vehicleMakeRequired;
  } else if (vehicleMake.length < MAKE_MIN) {
    errors.vehicleMake = SELLER_VALIDATION_CODES.vehicleMakeTooShort;
  } else if (vehicleMake.length > MAKE_MAX) {
    errors.vehicleMake = SELLER_VALIDATION_CODES.vehicleMakeTooLong;
  }

  const vehicleModel = normalized.vehicleModel;
  if (!vehicleModel) {
    errors.vehicleModel = SELLER_VALIDATION_CODES.vehicleModelRequired;
  } else if (vehicleModel.length > MODEL_MAX) {
    errors.vehicleModel = SELLER_VALIDATION_CODES.vehicleModelTooLong;
  }

  if (isBlankNumeric(input.vehicleYear) && normalized.vehicleYear == null) {
    errors.vehicleYear = SELLER_VALIDATION_CODES.vehicleYearRequired;
  } else if (normalized.vehicleYear != null) {
    const maxYear = getMaxVehicleYear();
    if (normalized.vehicleYear < YEAR_MIN) {
      errors.vehicleYear = SELLER_VALIDATION_CODES.vehicleYearTooOld;
    } else if (normalized.vehicleYear > maxYear) {
      errors.vehicleYear = SELLER_VALIDATION_CODES.vehicleYearTooFuture;
    }
  } else {
    errors.vehicleYear = SELLER_VALIDATION_CODES.vehicleYearRequired;
  }

  const vehicleTrim = normalized.vehicleTrim;
  if (vehicleTrim && vehicleTrim.length > TRIM_MAX) {
    errors.vehicleTrim = SELLER_VALIDATION_CODES.vehicleTrimTooLong;
  }

  if (isBlankNumeric(input.vehicleMileageKm) && normalized.vehicleMileageKm == null) {
    errors.vehicleMileageKm = SELLER_VALIDATION_CODES.vehicleMileageRequired;
  } else if (normalized.vehicleMileageKm == null) {
    errors.vehicleMileageKm = SELLER_VALIDATION_CODES.vehicleMileageInvalid;
  } else if (normalized.vehicleMileageKm > MILEAGE_MAX) {
    errors.vehicleMileageKm = SELLER_VALIDATION_CODES.vehicleMileageTooHigh;
  }

  if (isBlankNumeric(input.askingPrice) && normalized.askingPrice == null) {
    errors.askingPrice = SELLER_VALIDATION_CODES.askingPriceRequired;
  } else if (normalized.askingPrice == null || normalized.askingPrice <= 0) {
    errors.askingPrice = SELLER_VALIDATION_CODES.askingPriceInvalid;
  } else if (normalized.askingPrice > PRICE_MAX) {
    errors.askingPrice = SELLER_VALIDATION_CODES.askingPriceTooHigh;
  }

  const currency = normalized.currency || SELLER_DEFAULT_CURRENCY;
  if (!VALID_CURRENCIES.has(currency)) {
    errors.currency = SELLER_VALIDATION_CODES.currencyInvalid;
  }

  const rawOwnership = pickString(input.ownershipStatus);
  if (!rawOwnership || !VALID_OWNERSHIP_STATUSES.has(rawOwnership)) {
    errors.ownershipStatus = SELLER_VALIDATION_CODES.ownershipStatusRequired;
  }

  const conditionSummary = normalized.conditionSummary;
  if (conditionSummary) {
    if (conditionSummary.length > CONDITION_MAX) {
      errors.conditionSummary = SELLER_VALIDATION_CODES.conditionSummaryTooLong;
    } else if (isRepeatedCharacters(conditionSummary)) {
      errors.conditionSummary = SELLER_VALIDATION_CODES.conditionSummaryInvalid;
    }
  }

  const notes = normalized.notes;
  if (notes && notes.length > NOTES_MAX) {
    errors.notes = SELLER_VALIDATION_CODES.notesTooLong;
  }

  if (normalized.plate && normalized.plate.length > PLATE_MAX) {
    errors.plate = SELLER_VALIDATION_CODES.plateTooLong;
  }

  if (normalized.vin && normalized.vin.length > VIN_MAX) {
    errors.vin = SELLER_VALIDATION_CODES.vinTooLong;
  }

  if (!normalized.consentAccepted) {
    errors.consentAccepted = SELLER_VALIDATION_CODES.consentRequired;
  }

  const valid = Object.keys(errors).length === 0;

  return {
    valid,
    ok: valid,
    errors,
    fieldErrors: errors,
    formError: null,
    warnings,
    normalized,
  };
}

/**
 * Focus first invalid seller intake field (browser only).
 * @param {Record<string, string|null|undefined>} errors
 */
export function mapSellerSubmissionApiErrorsToFormErrors(apiFieldErrors = []) {
  const formErrors = {};
  const entries = Array.isArray(apiFieldErrors) ? apiFieldErrors : [];

  entries.forEach((entry) => {
    const path = entry?.path ? String(entry.path) : "";
    const message = entry?.message ? String(entry.message) : "";
    if (!path || !message) return;
    if (!formErrors[path]) {
      formErrors[path] = message;
    }
  });

  return formErrors;
}

/**
 * Focus first invalid seller intake field (browser only).
 * @param {Record<string, string|null|undefined>} errors
 */
export function focusFirstSellerSubmissionError(errors = {}) {
  const firstField = getFirstSellerSubmissionErrorField(errors);
  if (!firstField || typeof document === "undefined") return;
  const element = document.getElementById(FIELD_ID_MAP[firstField]);
  element?.focus?.();
  element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
}
